import ProductName from '@web/components/ProductName'
import { Link } from 'react-router-dom'

const NPS_NH_SAMPLE_PATH =
  'https://d2ufl17mx4sema.cloudfront.net/public/marketing/property/nps-nh-sample-report.pdf'

type Workflow = {
  audienceTag: string
  audience: string
  title: string
  trigger: React.ReactNode
  process: React.ReactNode
  outcome: React.ReactNode
  sample: React.ReactNode
}

const workflows: Workflow[] = [
  {
    audienceTag: 'For LIM teams',
    audience: 'LIM Officers, LIM Coordinators, Customer Services teams',
    title: 'LIM Hazard Section Report',
    trigger: (
      <>
        A LIM request lands against a property address, typically via your existing customer portal
        or property files system.
      </>
    ),
    process: (
      <>
        <ProductName /> retrieves the property&apos;s hazard profile from your council data layers,
        applies your LIM disclosure rules across the seven NPS-NH hazards plus any council-specific
        ones, and captures the data lineage in an audit trail. Your LIM Officer reviews, adds any
        property-specific commentary, and approves.
      </>
    ),
    outcome: (
      <>
        A LIM Hazard Section Report in the LGOIMA section 44B-compliant format, ready to append to
        the full LIM. Under five minutes from request to approved output. Every report carries the
        data lineage required under the October 2025 regulations. The archive is searchable,
        exportable, and survives staff turnover.
      </>
    ),
    sample: (
      <p className="text-sm text-brand-navy/70 italic m-0">
        Sample LIM Hazard Section Report available at walkthrough.
      </p>
    ),
  },
  {
    audienceTag: 'For consent planners',
    audience: 'Consent Planners, Resource Consent Officers, Planning Managers',
    title: 'NPS-NH Property Risk Report',
    trigger: (
      <>
        A resource consent application is lodged for an activity on a hazard-exposed site, picked up
        at section 88 completeness or section 95 notification screen.
      </>
    ),
    process: (
      <>
        <ProductName /> generates the property-level risk assessment automatically. Likelihood and
        consequence pulled from your hazard layers, classified against the Appendix 1 matrix as Low,
        Medium, High, or Very High. The 100-year climate-adjusted scenario runs alongside the
        present-day case (Policy 6). Data sources, model versions, and methodology are recorded for
        the Policy 5 audit trail. The consent planner reviews, applies proposal-specific judgement
        on mitigation and cascade risk, and produces the section 104 assessment.
      </>
    ),
    outcome: (
      <>
        An NPS-NH Property Risk Report that supports a defensible consent decision. <ProductName />{' '}
        removes 2 to 4 hours of data compilation per consent. The report attaches directly to the
        section 95 or 104 file.
      </>
    ),
    sample: (
      <div className="space-y-2">
        <a
          href={NPS_NH_SAMPLE_PATH}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 px-6 py-2.5 text-sm font-semibold text-white no-underline transition-colors rounded bg-brand-blue border-0 cursor-pointer hover:bg-brand-navy"
        >
          Download the sample report
        </a>
        <p className="text-xs text-brand-navy/70 m-0">
          Nine-page PDF &middot; 5.6 MB &middot; No mailing list.
        </p>
        <p className="text-xs text-brand-navy/80 m-0">
          <Link
            to="/products/property/methodology"
            className="text-brand-blue font-semibold no-underline hover:underline"
          >
            Read the full methodology →
          </Link>
        </p>
      </div>
    ),
  },
]

type StepProps = { label: string; children: React.ReactNode }

const Step = ({ label, children }: StepProps) => (
  <div className="space-y-1">
    <p className="text-xs font-bold uppercase tracking-widest text-brand-blue m-0">{label}</p>
    <p className="text-sm text-brand-navy leading-relaxed m-0">{children}</p>
  </div>
)

const ProductSummary = () => {
  return (
    <section className="bg-neutral-muted py-24 sm:py-16">
      <div className="space-y-12 mx-auto max-w-6xl px-6 lg:px-8">
        <div className="space-y-3 max-w-3xl">
          <span className="text-xs font-semibold uppercase tracking-widest text-brand-blue">
            Two outputs, one engine
          </span>
          <h2 className="text-4xl font-light text-brand-blue m-0">
            One data engine. Two compliant outputs.
          </h2>
          <p className="text-base text-brand-navy/90 leading-relaxed m-0">
            <ProductName /> runs two parallel workflows on the same underlying data. The LIM
            workflow serves your customer-facing team. The consent workflow serves your planners.
            Both pull council hazard data in automatically. Both come with a full audit trail.
          </p>
        </div>

        <div className="grid gap-6 md:grid-cols-2">
          {workflows.map((w) => (
            <article
              key={w.title}
              className="rounded-lg bg-white shadow-sm p-6 lg:p-8 space-y-6 flex flex-col"
            >
              <header className="space-y-2">
                <span className="text-xs font-semibold uppercase tracking-widest text-brand-blue">
                  {w.audienceTag}
                </span>
                <h3 className="text-2xl font-semibold text-brand-navy m-0">{w.title}</h3>
                <p className="text-sm text-brand-navy/70 m-0">{w.audience}</p>
              </header>

              <div className="space-y-4 flex-1">
                <Step label="Trigger">{w.trigger}</Step>
                <Step label="Process">{w.process}</Step>
                <Step label="Outcome">{w.outcome}</Step>
              </div>

              <div className="pt-2 border-0 border-t border-solid border-neutral-charcoal/15">
                <div className="pt-4">{w.sample}</div>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  )
}

export default ProductSummary
